<template>
    <div class="GlobalActionDocumentPanel">
        <div v-if="showCallout" class="GlobalActionDocumentPanel__callout">
            <EidIcon class="GlobalActionDocumentPanel__calloutIcon" :icon-src="warningIcon" />
            <span class="GlobalActionDocumentPanel__calloutText">
                <template v-if="entityName">
                    This document was submitted by <strong>{{ entityName }}</strong>, one of {{ entityCount }} entities affected.
                </template>
                <template v-else>
                    This document was submitted by one of the {{ entityCount }} entities affected.
                </template>
                {{ otherEntitiesSentence }}
                Your decision applies to all {{ entityCount }}.
            </span>
        </div>
        <div class="GlobalActionDocumentPanel__viewer">
            <LogoLoader v-if="loading" />
            <DocumentsViewer v-else :documents="docsStatus.list" />
        </div>
    </div>
</template>

<style lang="scss">
.GlobalActionDocumentPanel {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    height: 100%;

    &__callout {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 12px 14px;
        background: var(--eidWarningYellow-10);
        border: 1px solid var(--eidWarningYellow-50);
        border-radius: 8px;
        color: var(--eidQualityBlack);
        font-size: var(--text-small-size, 13px);
        line-height: var(--text-body-line-height, 1.5);
    }

    &__calloutIcon {
        flex-shrink: 0;
        width: 16px;
        height: 16px;
        margin-top: 2px;
    }

    &__calloutText strong {
        font-weight: var(--text-section-header-weight, 600);
    }

    &__viewer {
        display: flex;
        flex: 1;
        min-height: 0;

        /* Quasar's global .flex class wraps its children; without this a tall image in the
           viewer's column wraps into a second column beside the file pager. */

        .DocumentsViewer {
            flex-wrap: nowrap;
        }
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { LogoLoader } from '@evidentid/dashboard-commons/components/LogoLoader';
    import { ActionReview } from '@evidentid/tprm-portal-lib/models/entity-actions-review';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import DocumentsViewer from '@/modules/entity-actions-review/components/DocumentsViewer/DocumentsViewer.vue';
    import {
        CoiDocumentStatus,
        entityDisplayNameKey,
        useEntityActionsReviewStore,
    } from '@/modules/entity-actions-review/store';
    import {
        getAffectedEntityCount,
        getRepresentativeDocumentRequests,
        getRepresentativeEntityId,
    } from '@/modules/entity-actions-review/utils/globalActionScope';

    export default defineComponent({
        name: 'GlobalActionDocumentPanel',
        components: {
            DocumentsViewer,
            EidIcon,
            LogoLoader,
        },
        props: {
            actionReview: {
                type: Object as PropType<ActionReview>,
                required: true,
            },
        },
        data() {
            return {
                warningIcon: faTriangleExclamation,
            };
        },
        computed: {
            rpName(): string {
                return (this as any).$rp.current as string;
            },
            entityCount(): number {
                return getAffectedEntityCount(this.actionReview.evaluationResults);
            },
            representativeEntityId(): string | null {
                return getRepresentativeEntityId(this.actionReview.evaluationResults);
            },
            showCallout(): boolean {
                return this.entityCount > 1 && this.representativeEntityId !== null;
            },
            entityName(): string | null {
                if (this.representativeEntityId === null) {
                    return null;
                }
                const key = entityDisplayNameKey(this.rpName, this.representativeEntityId);
                return useEntityActionsReviewStore().entityDisplayNames[key] ?? null;
            },
            otherEntitiesSentence(): string {
                return this.entityCount - 1 === 1
                    ? 'The other entity submitted its own document, which isn’t shown and may differ.'
                    : `The other ${this.entityCount - 1} entities submitted their own documents, which aren’t shown and may differ.`;
            },
            docsStatus(): CoiDocumentStatus {
                const perAction = useEntityActionsReviewStore().rprDocuments[this.rpName];
                return perAction?.[this.actionReview.id] ?? { status: OperationStatus.uninitialized, list: [] };
            },
            loading(): boolean {
                return this.docsStatus.status === OperationStatus.loading;
            },
        },
        watch: {
            actionReview: {
                handler(): void {
                    this.loadRepresentative();
                },
                immediate: true,
            },
        },
        methods: {
            loadRepresentative(): void {
                const store = useEntityActionsReviewStore();
                const documentsFor = getRepresentativeDocumentRequests(this.actionReview.evaluationResults);
                if (this.docsStatus.status === OperationStatus.uninitialized && documentsFor.length > 0) {
                    store.loadRprDocuments({
                        rpName: this.rpName,
                        actionId: this.actionReview.id,
                        documentsFor,
                    });
                }
                if (this.representativeEntityId !== null) {
                    store.loadEntityDisplayName({ rpName: this.rpName, insuredId: this.representativeEntityId });
                }
            },
        },
    });
</script>
